import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import './AdminLogin.css';

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [code, setCode] = useState('');
  const [forgot, setForgot] = useState(false);
  const location = useLocation();
  // A message handed over by the reset-password page ("Your password has been changed...").
  const [notice, setNotice] = useState(location.state?.message || '');

  const { loginAdmin, verifyMfa, mfaRequired, logout, isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  const from = location.state?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, from]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await loginAdmin(formData.email.trim(), formData.password);

    if (result.success) {
      navigate(from, { replace: true });
    } else if (result.mfaRequired) {
      setLoading(false);
    } else {
      setError(result.message);
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verifyMfa(code);
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.message);
      setCode('');
      setLoading(false);
    }
  };

  // Forgot password: Supabase emails a one-time link to /admin/reset-password. The answer is the same whether or not
  // the address belongs to a staff account, so this form cannot be used to discover staff emails.
  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email.trim())) {
      setError('Enter your email address');
      return;
    }
    setLoading(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(formData.email.trim(), {
      redirectTo: `${window.location.origin}/admin/reset-password`
    });
    setLoading(false);
    if (resetError && resetError.status === 429) {
      setError('Too many requests. Please wait a few minutes and try again.');
      return;
    }
    if (resetError) console.error('Password reset request failed:', resetError.message);
    setNotice('If an account exists for that email, a link to choose a new password has been sent. It expires after one hour.');
  };

  const handleCancelMfa = async () => {
    setError('');
    setCode('');
    setFormData({ email: formData.email, password: '' });
    await logout();
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <motion.div
          className="admin-login-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="admin-login-header">
            <img src="/logo.png" alt="KSMS Logo" className="admin-logo" />
            <h1>Admin Portal</h1>
            <p>Sign in to access the admin dashboard</p>
          </div>

          {error && (
            <motion.div
              className="alert alert-error"
              role="alert"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {error}
            </motion.div>
          )}
          {notice && <div className="alert alert-success" role="status">{notice}</div>}

          {!mfaRequired && forgot ? (
            <form onSubmit={handleForgot} className="admin-login-form">
              <p>Enter your staff email address and we will send you a link to choose a new password.</p>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  autoComplete="username"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>
              <button type="submit" className="btn-login" disabled={loading}>
                {loading ? 'Sending...' : 'Send reset link'}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => { setForgot(false); setError(''); setNotice(''); }}
              >
                Back to sign in
              </button>
            </form>
          ) : mfaRequired ? (
            <form onSubmit={handleVerify} className="admin-login-form">
              <div className="form-group">
                <label htmlFor="mfa-code">Authentication code</label>
                <input
                  type="text"
                  id="mfa-code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                  autoFocus
                  disabled={loading}
                />
                <small>Enter the 6-digit code from your authenticator app.</small>
              </div>
              <button type="submit" className="btn-login" disabled={loading || code.length !== 6}>
                {loading ? 'Verifying...' : 'Verify'}
              </button>
              <button type="button" className="btn-login" onClick={handleCancelMfa} disabled={loading}>
                Cancel
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="admin-login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="username"
                value={formData.email}
                onChange={handleChange}
                required
                autoFocus
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="btn-login"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => { setForgot(true); setError(''); setNotice(''); }}
            >
              Forgot password?
            </button>
          </form>
          )}

        </motion.div>
      </div>
    </div>
  );
};

export default AdminLogin;
